// CB:BEGIN admin-add-email-bid-modal

import React, { useEffect, useMemo, useState } from 'react';

import { useApi } from '@/lib/useApi';

import { FaTimes } from 'react-icons/fa';

interface Contractor {

  id: string;

  company?: string;

  user: {

    email: string;

    firstName?: string;

    lastName?: string;

  };

}

interface AdminAddEmailBidModalProps {

  projectId: string;

  isOpen: boolean;

  onClose: () => void;

  onSuccess: () => void;

  /** Optional list of pre-verified contractor names (not in DB yet) */

  verifiedContractors?: string[];

  /** Callback when form validity changes */

  onFormValidityChange?: (isValid: boolean) => void;

  /** External trigger to submit the form */

  submitTrigger?: number;

}

interface FormState {

  contractorId: string;

  contractorBid: string; // dollars

  totalPrice: string; // mirrors contractorBid

  title: string;

  scopeNotes: string;

  warrantyLaborYears: string;

  warrantyMaterialYears: string;

  warrantyTransferable: boolean;

  warrantyNotes: string;

  estimatedStartDate: string;

  estimatedDurationDays: string;

  internalNotes: string;

}

const EMPTY_FORM: FormState = {

  contractorId: '',

  contractorBid: '',

  totalPrice: '',

  title: '',

  scopeNotes: '',

  warrantyLaborYears: '',

  warrantyMaterialYears: '',

  warrantyTransferable: false,

  warrantyNotes: '',

  estimatedStartDate: '',

  estimatedDurationDays: '',

  internalNotes: '',

};

export const AdminAddEmailBidModal: React.FC<AdminAddEmailBidModalProps> = ({

  projectId,

  isOpen,

  onClose,

  onSuccess,

  verifiedContractors = [],

  onFormValidityChange,

  submitTrigger,

}) => {

  const api = useApi();

  const [contractors, setContractors] = useState<Contractor[]>([]);

  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ---- Derived values -------------------------------------------------------
  // CB:BEGIN fix-contractor-gross-calculation
  // The field is labeled "Contractor Gross" which is 90% of the total bid
  // So what the user enters IS the contractor gross, and we need to calculate the total from that
  const contractorGross = useMemo(() => {
    const val = parseFloat(formData.contractorBid);
    return Number.isFinite(val) && val > 0 ? val : 0;
  }, [formData.contractorBid]);

  // Total bid = contractor gross / 0.9 (since contractor gross is 90% of total)
  const totalBidAmount = useMemo(() => {
    if (contractorGross === 0) return 0;
    return contractorGross / 0.9;
  }, [contractorGross]);

  const platformFee = useMemo(() => totalBidAmount * 0.1, [totalBidAmount]);

  const platformFeeDisplay = `$${platformFee.toFixed(2)}`;

  const contractorGrossDisplay = `$${contractorGross.toFixed(2)}`;

  // Homeowner sees the total bid amount (contractor gross + platform fee)
  const contractorNetToHomeownerDisplay = useMemo(() => {
    return `$${totalBidAmount.toFixed(2)}`;
  }, [totalBidAmount]);
  // CB:END fix-contractor-gross-calculation

  const getContractorName = (): string => {

    if (!formData.contractorId) return 'N/A';

    const isVerified = formData.contractorId.startsWith('verified-');

    if (isVerified) {

      return formData.contractorId.replace('verified-', '') + ' (Verified)';

    }

    const contractor = contractors.find((c) => c.id === formData.contractorId);

    if (!contractor) return 'Unknown contractor';

    return (

      contractor.company ||

      `${contractor.user.firstName || ''} ${contractor.user.lastName || ''}`.trim() ||

      contractor.user.email

    );

  };

  // ---- Data loading ---------------------------------------------------------

  const fetchContractors = async () => {

    setError(null);

    const normalizeResponse = (data: any): Contractor[] => {

      if (!data) return [];

      if (Array.isArray(data)) return data;

      if (Array.isArray(data.contractors)) return data.contractors;

      return [];

    };

    try {

      // Try dev endpoint first (more reliable in server-simple.js)

      const data = await api.get('/api/admin/contractors');

      setContractors(normalizeResponse(data));

    } catch (devErr) {

      console.warn('⚠️ /api/admin/contractors failed, trying fallback...', devErr);

      try {

        // Fallback to production admin endpoint

        const data = await api.get('/api/admin/contractors');

        setContractors(normalizeResponse(data));

      } catch (adminErr) {

        console.error('Error fetching contractors:', adminErr);

        setContractors([]);

        setError('Failed to load contractors. Please refresh and try again.');

      }

    }

  };

  useEffect(() => {

    if (isOpen) {

      setFormData(EMPTY_FORM);

      setError(null);

      setShowConfirmModal(false);

      fetchContractors();

    }

  }, [isOpen]);

  // ---- Form helpers ---------------------------------------------------------

  const isFormValid = useMemo(() => {

    return !!formData.contractorId && 

           !!formData.contractorBid && 

           contractorGross > 0;

  }, [formData.contractorId, formData.contractorBid, contractorGross]);

  // Notify parent of form validity changes

  useEffect(() => {

    if (onFormValidityChange) {

      onFormValidityChange(isFormValid);

    }

  }, [isFormValid, onFormValidityChange]);

  // Handle external submit trigger

  useEffect(() => {

    if (submitTrigger && submitTrigger > 0 && isFormValid && isOpen && !showConfirmModal) {

      // Trigger form submission

      setError(null);

      if (!formData.contractorId || !formData.contractorBid || contractorGross <= 0) {

        return;

      }

      setShowConfirmModal(true);

    }

  }, [submitTrigger, isFormValid, isOpen, showConfirmModal, formData.contractorId, formData.contractorBid, contractorGross]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {

    setFormData((prev) => {

      const updated = { ...prev, [key]: value };

      // Keep totalPrice in sync with contractorBid

      if (key === 'contractorBid') {

        updated.totalPrice = value as string;

      }

      return updated;

    });

  };

  // ---- Submit handlers ------------------------------------------------------

  // First submit: validate and show confirmation modal

  const handleSubmit = (e: React.FormEvent) => {

    e.preventDefault();

    setError(null);

    if (!formData.contractorId) {

      setError('Please select a contractor (or verified contractor).');

      return;

    }

    if (!formData.contractorBid) {

      setError('Please enter a contractor bid amount.');

      return;

    }

    if (!contractorGross || contractorGross <= 0) {

      setError('Contractor gross must be a valid positive number.');

      return;

    }

    setShowConfirmModal(true);

  };

  // Second submit: confirmed, actually hit the API

  const handleConfirmSubmit = async () => {

    if (loading) return;

    setLoading(true);

    setError(null);

    try {

      // CB:BEGIN fix-bid-amount-calculation
      // Use totalBidAmount (calculated from contractor gross) instead of contractorBidNumber
      const totalPriceCents = Math.round(totalBidAmount * 100);
      // CB:END fix-bid-amount-calculation

      const isVerified = formData.contractorId.startsWith('verified-');

      const contractorName = isVerified ? formData.contractorId.replace('verified-', '') : null;

      const payload: any = {

        totalPrice: totalPriceCents,

        currency: 'USD',

      };

      if (isVerified) {

        payload.contractorName = contractorName;

      } else {

        payload.contractorId = formData.contractorId;

      }

      if (formData.title) payload.title = formData.title;

      if (formData.scopeNotes) payload.scopeNotes = formData.scopeNotes;

      if (formData.warrantyLaborYears) {

        payload.warrantyLaborYears = parseInt(formData.warrantyLaborYears, 10);

      }

      if (formData.warrantyMaterialYears) {

        payload.warrantyMaterialYears = parseInt(formData.warrantyMaterialYears, 10);

      }

      if (formData.warrantyTransferable) {

        payload.warrantyTransferable = true;

      }

      if (formData.warrantyNotes) payload.warrantyNotes = formData.warrantyNotes;

      if (formData.estimatedStartDate) {

        payload.estimatedStartDate = formData.estimatedStartDate;

      }

      if (formData.estimatedDurationDays) {

        payload.estimatedDurationDays = parseInt(formData.estimatedDurationDays, 10);

      }

      if (formData.internalNotes) payload.internalNotes = formData.internalNotes;

      // Use unified endpoint that supports both registered and verified contractors
      // Try dev endpoint first, fallback to production admin endpoint
      try {
        await api.post(`/api/projects/${projectId}/bids`, payload);
      } catch (devErr: any) {
        console.warn('⚠️ /api/projects/:id/bids failed, trying fallback...', devErr);
        // Fallback to production admin endpoint
        try {
          await api.post(`/api/admin/projects/${projectId}/bids`, payload);
        } catch (adminErr: any) {
          console.error('Error creating bid:', adminErr);
          const errorMsg = adminErr?.message || 'Failed to create bid';
          throw new Error(errorMsg);
        }
      }

      setFormData(EMPTY_FORM);

      setShowConfirmModal(false);

      // Show success message
      setSuccessMessage('Bid submitted successfully!');

      setTimeout(() => {
        setSuccessMessage(null);
        onSuccess();
        onClose();
      }, 2000);

    } catch (err: any) {

      console.error('Error creating bid:', err);

      setError(err?.message || 'Failed to create bid. Please try again.');

      setShowConfirmModal(false);

    } finally {

      setLoading(false);

    }

  };

  if (!isOpen && !showConfirmModal && !successMessage) return null;

  // ---- Render ---------------------------------------------------------------

  return (

    <>
    {isOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6 py-4">

      {/* Backdrop */}

      <div

        className="fixed inset-0 bg-black/50"

        onClick={() => {

          if (!loading) onClose();

        }}

      />

      {/* Modal */}

      <div className="relative z-50 w-full max-w-6xl h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden">

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-8 py-6">

          {/* Header */}

          <div className="mb-4 flex items-center justify-between">

            <h3 className="text-lg font-semibold text-gray-900">Add Bid</h3>

            <button

              type="button"

              onClick={onClose}

              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"

            >

              <FaTimes className="h-4 w-4" />

            </button>

          </div>

          {/* Description */}

          <p className="mb-4 text-sm text-gray-600">

            Create a bid on behalf of a contractor and send it to the homeowner. You'll be able to review the

            details before it's emailed.

          </p>

          {/* Error banner */}

          {error && (

            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">

              {error}

            </div>

          )}

          {/* Form */}

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Contractor & Bid */}

            <div className="grid gap-4 md:grid-cols-2">

              {/* Contractor Selection */}

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">

                  Contractor <span className="text-red-500">*</span>

                </label>

                <select

                  required

                  value={formData.contractorId}

                  onChange={(e) => updateField('contractorId', e.target.value)}

                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                >

                  <option value="">Select a contractor</option>

                  {/* Verified contractors */}

                  {verifiedContractors.length > 0 && (

                    <optgroup label="Verified Contractors">

                      {verifiedContractors.map((name, index) => (

                        <option key={`verified-${index}`} value={`verified-${name}`}>

                          {name} (Verified)

                        </option>

                      ))}

                    </optgroup>

                  )}

                  {/* Registered contractors */}

                  {contractors.length > 0 && (

                    <optgroup label="Registered Contractors">

                      {contractors.map((contractor) => (

                        <option key={contractor.id} value={contractor.id}>

                          {contractor.company ||

                            `${contractor.user.firstName || ''} ${contractor.user.lastName || ''}`.trim() ||

                            contractor.user.email}

                        </option>

                      ))}

                    </optgroup>

                  )}

                </select>

              </div>

              {/* Contractor Bid */}

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">

                  Contractor Bid (USD) <span className="text-red-500">*</span>

                </label>

                <input

                  type="number"

                  step="0.01"

                  min="0"

                  required

                  value={formData.contractorBid}

                  onChange={(e) => updateField('contractorBid', e.target.value)}

                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  placeholder="0.00"

                />

                <p className="mt-1 text-xs text-gray-500">The total bid amount from the contractor.</p>

              </div>

            </div>

            {/* Pricing summary */}

            <div className="grid gap-4 md:grid-cols-3">

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">Platform Fee (10%)</label>

                <input

                  type="text"

                  readOnly

                  value={platformFeeDisplay}

                  className="block w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 shadow-sm"

                />

                <p className="mt-1 text-xs text-gray-500">

                  For display only. Fee is calculated from the contractor bid.

                </p>

              </div>

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">Contractor Gross (USD)</label>

                <input

                  type="text"

                  readOnly

                  value={contractorGrossDisplay}

                  className="block w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 shadow-sm"

                />

                <p className="mt-1 text-xs text-gray-500">Amount contractor receives after 10% platform fee (90% of bid).</p>

              </div>

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">

                  Homeowner Sees (USD)

                </label>

                <input

                  type="text"

                  readOnly

                  value={contractorNetToHomeownerDisplay}

                  className="block w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 shadow-sm"

                />

                <p className="mt-1 text-xs text-gray-500">Displayed bid amount in the homeowner email.</p>

              </div>

            </div>

            {/* Scope & Title */}

            <div className="space-y-4">

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">Bid Title (optional)</label>

                <input

                  type="text"

                  value={formData.title}

                  onChange={(e) => updateField('title', e.target.value)}

                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  placeholder="Example: Complete tear-off and replacement with architectural shingles"

                />

              </div>

              <div>

                <label className="mb-1 block text-sm font-medium text-gray-700">Scope Notes</label>

                <textarea

                  rows={3}

                  value={formData.scopeNotes}

                  onChange={(e) => updateField('scopeNotes', e.target.value)}

                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  placeholder="Describe what's included/excluded in this bid."

                />

              </div>

            </div>

            {/* Warranty */}

            <div className="border-t pt-4">

              <h4 className="mb-3 text-sm font-medium text-gray-900">Warranty Information</h4>

              <div className="grid gap-4 md:grid-cols-2">

                <div>

                  <label className="mb-1 block text-sm font-medium text-gray-700">Labor Warranty (Years)</label>

                  <input

                    type="number"

                    min="0"

                    value={formData.warrantyLaborYears}

                    onChange={(e) => updateField('warrantyLaborYears', e.target.value)}

                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  />

                </div>

                <div>

                  <label className="mb-1 block text-sm font-medium text-gray-700">

                    Material Warranty (Years)

                  </label>

                  <input

                    type="number"

                    min="0"

                    value={formData.warrantyMaterialYears}

                    onChange={(e) => updateField('warrantyMaterialYears', e.target.value)}

                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  />

                </div>

                <div className="flex items-center space-x-2">

                  <input

                    id="warrantyTransferable"

                    type="checkbox"

                    checked={formData.warrantyTransferable}

                    onChange={(e) => updateField('warrantyTransferable', e.target.checked)}

                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"

                  />

                  <label htmlFor="warrantyTransferable" className="text-sm text-gray-700">

                    Warranty is transferable

                  </label>

                </div>

                <div>

                  <label className="mb-1 block text-sm font-medium text-gray-700">Warranty Notes</label>

                  <textarea

                    rows={2}

                    value={formData.warrantyNotes}

                    onChange={(e) => updateField('warrantyNotes', e.target.value)}

                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  />

                </div>

              </div>

            </div>

            {/* Timing & Internal Notes */}

            <div className="border-t pt-4">

              <h4 className="mb-3 text-sm font-medium text-gray-900">Timing & Internal Notes</h4>

              <div className="grid gap-4 md:grid-cols-2">

                <div>

                  <label className="mb-1 block text-sm font-medium text-gray-700">

                    Estimated Start Date

                  </label>

                  <input

                    type="date"

                    value={formData.estimatedStartDate}

                    onChange={(e) => updateField('estimatedStartDate', e.target.value)}

                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  />

                </div>

                <div>

                  <label className="mb-1 block text-sm font-medium text-gray-700">

                    Estimated Duration (Days)

                  </label>

                  <input

                    type="number"

                    min="0"

                    value={formData.estimatedDurationDays}

                    onChange={(e) => updateField('estimatedDurationDays', e.target.value)}

                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  />

                </div>

              </div>

              <div className="mt-4">

                <label className="mb-1 block text-sm font-medium text-gray-700">

                  Internal Notes (not visible to homeowner)

                </label>

                <textarea

                  rows={2}

                  value={formData.internalNotes}

                  onChange={(e) => updateField('internalNotes', e.target.value)}

                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

                  placeholder="Internal notes for your team only."

                />

              </div>

            </div>

            {/* Actions */}

            <div className="mt-6 flex justify-end space-x-3 border-t pt-4">

              <button

                type="button"

                onClick={onClose}

                disabled={loading}

                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"

              >

                Cancel

              </button>

              <button

                type="submit"

                disabled={loading}

                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"

              >

                {loading ? 'Submitting...' : 'Submit Bid'}

              </button>

            </div>

          </form>

          </div>
        </div>

      </div>
    </div>
    )}
      {/* CB:BEGIN confirmation-modal */}

      {showConfirmModal && !successMessage && (

        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => !loading && setShowConfirmModal(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Confirm Bid Details</h3>
            </div>

            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#6b7280' }}>

              You're about to send this bid to the homeowner. Please confirm that the details below are correct.

            </p>

            <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Contractor</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{getContractorName()}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Contractor Gross (90%)</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{contractorGrossDisplay}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Total Bid (Homeowner Sees)</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{contractorNetToHomeownerDisplay}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Platform Fee (10%)</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{platformFeeDisplay}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Contractor Gross (90%)</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{contractorGrossDisplay}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Homeowner Sees</span>
                <span style={{ fontWeight: '600', color: '#111827' }}>{contractorNetToHomeownerDisplay}</span>
              </div>

            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>

              <button

                type="button"

                onClick={() => setShowConfirmModal(false)}

                disabled={loading}

                style={{

                  padding: '10px 20px',

                  borderRadius: '8px',

                  fontSize: '14px',

                  fontWeight: '600',

                  border: '1px solid #d1d5db',

                  background: 'white',

                  color: '#374151',

                  cursor: loading ? 'not-allowed' : 'pointer',

                  transition: 'all 0.2s',

                  opacity: loading ? 0.5 : 1

                }}

                onMouseEnter={(e) => {

                  if (!loading) e.currentTarget.style.background = '#f9fafb';

                }}

                onMouseLeave={(e) => {

                  if (!loading) e.currentTarget.style.background = 'white';

                }}

              >

                Cancel

              </button>

              <button

                type="button"

                onClick={handleConfirmSubmit}

                disabled={loading}

                style={{

                  padding: '10px 20px',

                  borderRadius: '8px',

                  fontSize: '14px',

                  fontWeight: '600',

                  border: 'none',

                  background: loading ? '#9ca3af' : '#10b981',

                  color: 'white',

                  cursor: loading ? 'not-allowed' : 'pointer',

                  transition: 'all 0.2s',

                  opacity: loading ? 0.7 : 1

                }}

                onMouseEnter={(e) => {

                  if (!loading) e.currentTarget.style.background = '#059669';

                }}

                onMouseLeave={(e) => {

                  if (!loading) e.currentTarget.style.background = '#10b981';

                }}

              >

                {loading ? 'Submitting...' : 'Confirm & Send to Homeowner'}

              </button>

            </div>

          </div>

        </div>

      )}

      {/* CB:END confirmation-modal */}

      {/* Success Message Modal */}
      {successMessage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '32px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
              Bid Submitted Successfully!
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              The bid has been sent to the homeowner.
            </p>
          </div>
        </div>
      )}

    </>
  );

};

// CB:END admin-add-email-bid-modal
